import type { ChangeEvent, FC } from 'react';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { SingleValue } from 'react-select';

import { _isEmpty, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';

import type { components } from '@/generated/internal-api/openapi-types';
import InVivoError from '@/model/InVivoError.ts';
import type { StudyApiId } from '@/model/Study.model';

import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';

import Loading from '@/components/Loading';
import Radio from '@/components/UI/FormElements/Radio/Radio';
import InlineBanner from '@/components/UI/InlineBanner/InlineBanner';
import Select from '@/components/UI/Select/Select';

import {
  EXCLUDED_ANIMAL_DETAIL_LIMIT,
  type ExcludedAnimalError,
  groupExcludedAnimalErrors,
} from './ImportAnimals.utils';
import styles from './ImportAnimals.module.scss';

type ColonyImportType = components['schemas']['ColonyImportType.schema'];
type ExternalColonyImportV1 = components['schemas']['ExternalColonyImportV1.schema'];
type ImportIdField = keyof Omit<ExternalColonyImportV1, 'import_type'>;

interface IdTypeOption {
  value: ImportIdField;
  label: string;
}

interface ImportSummary {
  animals: number;
  cages: number;
}

const breedingIdOptions: Array<IdTypeOption> = [
  { value: 'animal_id', label: 'Animal number' },
  { value: 'order_id', label: 'Order number' },
  { value: 'project_id', label: 'Project number' },
];

const experimentalIdOptions: Array<IdTypeOption> = [
  { value: 'batch_id', label: 'Batch number' },
  { value: 'order_id', label: 'Order number' },
  { value: 'project_id', label: 'Project number' },
];

interface ImportAnimalsProps {
  onComplete: () => void;
  studyApiId: StudyApiId;
  testId?: string;
}

/**
 * Modal for importing animals into a study from the tick@lab external colony, reporting
 * the created entity counts and any animals the import had to exclude.
 */
const ImportAnimals: FC<ImportAnimalsProps> = ({ studyApiId, onComplete, testId = 'import-animals' }) => {
  const dispatch = useDispatch();
  const [stockType, setStockType] = useState<ColonyImportType>('breeding');
  const [importIdField, setImportIdField] = useState<ImportIdField>('animal_id');
  const [value, setValue] = useState('');
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [excludedAnimalErrors, setExcludedAnimalErrors] = useState<Array<ExcludedAnimalError>>([]);
  const [errors, setErrors] = useState<Array<string>>([]);

  const showExcludedAnimalDetail = excludedAnimalErrors.length <= EXCLUDED_ANIMAL_DETAIL_LIMIT;
  const showImportSummary =
    _notNil(importSummary) && (importSummary.animals > 0 || importSummary.cages > 0 || _isEmpty(excludedAnimalErrors));
  const groupedExcludedAnimalErrors = useMemo(
    () => (showExcludedAnimalDetail ? [] : groupExcludedAnimalErrors(excludedAnimalErrors)),
    [excludedAnimalErrors, showExcludedAnimalDetail]
  );

  const { loading: importLoading, invoke: invokeImport } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/external-colony/import',
    invokeOnInit: false,
    path: { studyApiId },
  });

  const closeModal = () => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  };

  const onChangeValue = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };
  const handleStockTypeChange = (newStockType: ColonyImportType) => {
    setStockType(newStockType);
    if (newStockType === 'breeding') {
      setImportIdField('animal_id');
    } else {
      setImportIdField('batch_id');
    }
    setValue('');
  };

  const handleIdFieldChange = (option: SingleValue<IdTypeOption>) => {
    if (_notNil(option)) {
      setImportIdField(option.value);
      setValue('');
    }
  };

  const currentIdOptions = stockType === 'breeding' ? breedingIdOptions : experimentalIdOptions;
  const selectedIdOption = currentIdOptions.find((option) => option.value === importIdField) ?? currentIdOptions[0];

  const onSubmit = async () => {
    setImportSummary(null);
    setExcludedAnimalErrors([]);
    setErrors([]);

    try {
      const requestBody: ExternalColonyImportV1 = {
        import_type: stockType,
        [importIdField]: value,
      };
      const response = await invokeImport({
        body: requestBody,
        path: { studyApiId },
      });

      const responseBody = response.body;
      if (_notNil(responseBody) && responseBody.success === true) {
        setImportSummary({ animals: responseBody.animals ?? 0, cages: responseBody.cages ?? 0 });
        setExcludedAnimalErrors(responseBody.excluded_animal_errors ?? []);
      } else if (_notNil(responseBody) && _notNil(responseBody.errors)) {
        setErrors(responseBody.errors);
      }

      onComplete();
    } catch (error) {
      setImportSummary(null);
      ExceptionHandler.captureException(
        new InVivoError('Could not import from external colony', {
          cause: error,
          slug: 'import-external-animals',
        })
      );
    }
  };

  return (
    <ModalContainer size="small" testKey={testId}>
      <ModalHeader title="Import from tick@lab" closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />
      {importLoading ? (
        <Loading txt="Importing from tick@lab" />
      ) : (
        <div className="pv3 ph3" data-testid={`${testId}__form`}>
          <div className="mb3">
            <Radio
              id="breeding"
              key="breeding"
              checked={stockType === 'breeding'}
              onChange={() => handleStockTypeChange('breeding')}
              name="stockType"
              label="Breeding stock"
              value="breeding"
              className="mb2"
              dataTestId={`${testId}__stock-type-breeding`}
            />
            {stockType === 'breeding' && (
              <div className="ml4 mb3" style={{ width: '340px' }} data-testid={`${testId}__breeding-options`}>
                <Select<IdTypeOption>
                  className="w-100 mb2"
                  options={breedingIdOptions}
                  value={selectedIdOption}
                  onChange={handleIdFieldChange}
                  isMulti={false}
                  isSearchable={false}
                  data-testid={`${testId}__id-type-select`}
                />
                <input
                  type="text"
                  placeholder="Number"
                  onChange={onChangeValue}
                  value={value}
                  autoFocus={true}
                  className="w-100"
                  data-testid={`${testId}__id-input`}
                />
              </div>
            )}
          </div>

          <div className="mb3">
            <Radio
              id="experimental"
              key="experimental"
              checked={stockType === 'experimental'}
              onChange={() => handleStockTypeChange('experimental')}
              name="stockType"
              label="Experimental stock"
              value="experimental"
              className="mb2"
              dataTestId={`${testId}__stock-type-experimental`}
            />
            {stockType === 'experimental' && (
              <div className="ml4 mb3" style={{ width: '340px' }} data-testid={`${testId}__experimental-options`}>
                <Select<IdTypeOption>
                  className="w-100 mb2"
                  options={experimentalIdOptions}
                  value={selectedIdOption}
                  onChange={handleIdFieldChange}
                  isMulti={false}
                  isSearchable={false}
                  data-testid={`${testId}__id-type-select`}
                />
                <input
                  type="text"
                  placeholder="Number"
                  onChange={onChangeValue}
                  value={value}
                  autoFocus={true}
                  className="w-100"
                  data-testid={`${testId}__id-input`}
                />
              </div>
            )}
          </div>

          {showImportSummary && (
            <InlineBanner
              variant="success"
              title="Import complete"
              className="mb2"
              testId={`${testId}__success-message`}
            >
              <p className="f5 ma0 lh-copy">
                We have created the following entities from <span className="fw6">tick@lab</span>:
              </p>
              <ul className={styles.bulletList}>
                <li className="f5">
                  <span className="fw6">Animals</span>: {importSummary.animals}
                </li>
                <li className="f5">
                  <span className="fw6">Cages</span>: {importSummary.cages}
                </li>
              </ul>
            </InlineBanner>
          )}
          {_isNotEmpty(excludedAnimalErrors) && (
            <InlineBanner
              variant="warning"
              title="Some animals were excluded due to:"
              testId={`${testId}__excluded-animals`}
            >
              {showExcludedAnimalDetail ? (
                <ul className={styles.bulletList} data-testid={`${testId}__excluded-animals-detailed`}>
                  {excludedAnimalErrors.map(({ animal_name: animalName, errors: animalErrors = [] }, index) => (
                    <li key={index} className="f5 mb1" data-test-key={animalName ?? String(index)}>
                      {_isNotBlank(animalName) && (
                        <>
                          <span className="fw6">{animalName}</span>:
                        </>
                      )}
                      {_isNotEmpty(animalErrors) && (
                        <ul className={styles.nestedBulletList}>
                          {animalErrors.map((message, messageIndex) => (
                            <li key={messageIndex} className="f5">
                              {message}
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className={styles.bulletList} data-testid={`${testId}__excluded-animals-grouped`}>
                  {groupedExcludedAnimalErrors.map(({ message, animalCount }) => (
                    <li key={message} className="f5" data-test-key={message}>
                      <span className="fw6">{`${animalCount} ${animalCount === 1 ? 'Animal' : 'Animals'}`}</span>:{' '}
                      {message}
                    </li>
                  ))}
                </ul>
              )}
            </InlineBanner>
          )}
          {_isNotEmpty(errors) && (
            <InlineBanner
              variant="critical"
              title="There was an error importing from tick@lab"
              testId={`${testId}__error-message`}
            >
              <ul className={styles.bulletList}>
                {errors.map((error, index) => (
                  <li key={index} className="f5">
                    {error}
                  </li>
                ))}
              </ul>
            </InlineBanner>
          )}
        </div>
      )}
      <ModalActions
        onSubmit={onSubmit}
        submitButtonProps={{ disabled: _isEmpty(value), testId: `${testId}__submit-button` }}
        submitBtnText="Import"
        className="pa3 bt"
        onCancel={closeModal}
        cancelButtonProps={{ testId: `${testId}__cancel-button` }}
      />
    </ModalContainer>
  );
};

export default ImportAnimals;
